import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  FaArrowLeft, FaGem, FaEuroSign, FaChartLine, FaUsers,
  FaSearch, FaCalendarAlt, FaEye, FaCheckCircle, FaTimesCircle,
  FaHourglassHalf, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import { adminAPI } from '../../services/api';

const AdminPayments = () => {
  const { user } = useSelector((state) => state.auth);

  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [stats, setStats] = useState({
    total_revenue: 0,
    monthly_revenue: 0,
    total_transactions: 0,
    total_buyers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (searchTerm) params.search = searchTerm;
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await adminAPI.getDiamantTransactions(params);
      const data = res.data;
      setTransactions(data.transactions.data || []);
      setPagination({
        current_page: data.transactions.current_page,
        last_page: data.transactions.last_page,
        total: data.transactions.total,
      });
      setStats(data.stats);
    } catch (error) {
      console.error('Error fetching diamant transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus, dateFrom, dateTo]);

  useEffect(() => {
    fetchData(currentPage);
  }, [fetchData, currentPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const map = {
      completed: 'badge bg-success',
      pending: 'badge bg-warning text-dark',
      failed: 'badge bg-danger',
      cancelled: 'badge bg-secondary',
    };
    return map[status] || 'badge bg-secondary';
  };

  const getStatusText = (status) => {
    const map = {
      completed: 'Complété',
      pending: 'En attente',
      failed: 'Échoué',
      cancelled: 'Annulé',
    };
    return map[status] || status;
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <FaCheckCircle className="text-success me-1" />;
    if (status === 'pending') return <FaHourglassHalf className="text-warning me-1" />;
    return <FaTimesCircle className="text-danger me-1" />;
  };

  const formatPrice = (meta) => {
    if (!meta) return '-';
    const cents = meta.price_cents;
    const currency = (meta.currency || 'EUR').toUpperCase();
    if (cents == null) return '-';
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="admin-payments min-vh-100" style={{ backgroundColor: 'var(--chatoo-bg-secondary)' }}>
      <div className="container-fluid py-4">

        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-secondary me-3"
                  onClick={() => window.location.href = '/admin/dashboard'}
                >
                  <FaArrowLeft className="me-2" />
                  Retour
                </button>
                <div>
                  <h1 className="h2 mb-1">Recharges Diamant</h1>
                  <p className="mb-0" style={{ color: 'var(--chatoo-text-secondary)' }}>
                    Historique des achats de Diamant
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaEuroSign className="text-success mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.total_revenue.toFixed(2)} €</h4>
                <small style={{ color: 'var(--chatoo-text-secondary)' }}>Revenus Totaux</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaChartLine className="text-primary mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.monthly_revenue.toFixed(2)} €</h4>
                <small style={{ color: 'var(--chatoo-text-secondary)' }}>Revenus ce Mois</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaGem className="text-info mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.total_transactions}</h4>
                <small style={{ color: 'var(--chatoo-text-secondary)' }}>Transactions Totales</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaUsers className="text-warning mb-2" size={24} />
                <h4 className="h5 mb-1">{stats.total_buyers}</h4>
                <small style={{ color: 'var(--chatoo-text-secondary)' }}>Acheteurs Uniques</small>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <form onSubmit={handleSearch}>
              <div className="row g-2 align-items-end">
                <div className="col-md-4">
                  <label className="form-label small mb-1">Rechercher</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaSearch /></span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Utilisateur, email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Statut</label>
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">Tous</option>
                    <option value="completed">Complété</option>
                    <option value="pending">En attente</option>
                    <option value="failed">Échoué</option>
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Du</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Au</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="col-md-2 d-flex gap-2">
                  <button type="submit" className="btn btn-primary flex-grow-1">Filtrer</button>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleResetFilters} title="Réinitialiser">✕</button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold">Transactions</span>
            <small style={{ color: 'var(--chatoo-text-secondary)' }}>{pagination.total} résultat(s)</small>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th className="border-0 ps-4">Utilisateur</th>
                      <th className="border-0">Package</th>
                      <th className="border-0">Diamants</th>
                      <th className="border-0">Prix</th>
                      <th className="border-0">Statut</th>
                      <th className="border-0">Date</th>
                      <th className="border-0 pe-4">Détails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td className="ps-4">
                          {tx.user ? (
                            <div>
                              <strong>{tx.user.first_name} {tx.user.last_name}</strong>
                              <br />
                              <small style={{ color: 'var(--chatoo-text-secondary)' }}>@{tx.user.username}</small>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-primary">
                            {tx.metadata?.package_name || 'Package inconnu'}
                          </span>
                        </td>
                        <td>
                          <span className="fw-semibold text-info">
                            <FaGem className="me-1" size={12} />
                            {tx.amount}
                          </span>
                        </td>
                        <td>
                          <strong>{formatPrice(tx.metadata)}</strong>
                        </td>
                        <td>
                          <span className={getStatusBadge(tx.status)}>
                            {getStatusIcon(tx.status)}
                            {getStatusText(tx.status)}
                          </span>
                        </td>
                        <td>
                          <small style={{ color: 'var(--chatoo-text-secondary)' }}>
                            <FaCalendarAlt className="me-1" />
                            {formatDate(tx.created_at)}
                          </small>
                        </td>
                        <td className="pe-4">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            title="Voir détails"
                            onClick={() => { setSelectedTransaction(tx); setShowDetailsModal(true); }}
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {transactions.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          Aucune transaction trouvée
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="card-footer d-flex justify-content-between align-items-center">
              <small style={{ color: 'var(--chatoo-text-secondary)' }}>
                Page {pagination.current_page} / {pagination.last_page}
              </small>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={pagination.current_page === 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                >
                  <FaChevronLeft />
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={pagination.current_page === pagination.last_page}
                  onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaGem className="text-info me-2" />
                  Détails de la Transaction
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <h6 className="border-bottom pb-2 mb-3">Utilisateur</h6>
                    {selectedTransaction.user ? (
                      <>
                        <p className="mb-1"><strong>Nom :</strong> {selectedTransaction.user.first_name} {selectedTransaction.user.last_name}</p>
                        <p className="mb-1"><strong>Username :</strong> @{selectedTransaction.user.username}</p>
                        <p className="mb-1"><strong>Email :</strong> {selectedTransaction.user.email}</p>
                      </>
                    ) : (
                      <p className="text-muted">Utilisateur inconnu</p>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <h6 className="border-bottom pb-2 mb-3">Transaction</h6>
                    <p className="mb-1"><strong>ID :</strong> <small className="font-monospace">{selectedTransaction.id}</small></p>
                    <p className="mb-1"><strong>Statut :</strong> <span className={getStatusBadge(selectedTransaction.status)}>{getStatusText(selectedTransaction.status)}</span></p>
                    <p className="mb-1"><strong>Date :</strong> {formatDate(selectedTransaction.created_at)}</p>
                    {selectedTransaction.processed_at && (
                      <p className="mb-1"><strong>Traité le :</strong> {formatDate(selectedTransaction.processed_at)}</p>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <h6 className="border-bottom pb-2 mb-3">Package</h6>
                    <p className="mb-1"><strong>Nom :</strong> {selectedTransaction.metadata?.package_name || '—'}</p>
                    <p className="mb-1"><strong>Prix :</strong> {formatPrice(selectedTransaction.metadata)}</p>
                    <p className="mb-1">
                      <strong>Diamants crédités :</strong>
                      <span className="text-info ms-1 fw-semibold">
                        <FaGem className="me-1" size={12} />{selectedTransaction.amount}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <h6 className="border-bottom pb-2 mb-3">Solde Wallet</h6>
                    <p className="mb-1"><strong>Avant :</strong> {selectedTransaction.balance_before} <FaGem size={10} className="text-info" /></p>
                    <p className="mb-1"><strong>Après :</strong> {selectedTransaction.balance_after} <FaGem size={10} className="text-info" /></p>
                    {selectedTransaction.description && (
                      <p className="mb-1"><strong>Description :</strong> <small>{selectedTransaction.description}</small></p>
                    )}
                  </div>
                </div>
                {selectedTransaction.metadata?.stripe_payment_intent_id && (
                  <div className="row">
                    <div className="col-12">
                      <h6 className="border-bottom pb-2 mb-3">Stripe</h6>
                      <p className="mb-1"><strong>Payment Intent :</strong> <small className="font-monospace">{selectedTransaction.metadata.stripe_payment_intent_id}</small></p>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .table th {
          font-weight: 600 !important;
          font-size: 0.875rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          background: var(--chatoo-bg-secondary) !important;
          color: var(--chatoo-text-primary) !important;
          border-bottom: 2px solid var(--chatoo-gray-light) !important;
        }
        .table td {
          vertical-align: middle !important;
          background: var(--chatoo-bg-primary) !important;
          color: var(--chatoo-text-primary) !important;
        }
        .table tbody tr:hover td {
          background: var(--chatoo-bg-secondary) !important;
        }
        .modal-content {
          background: var(--chatoo-bg-primary) !important;
          color: var(--chatoo-text-primary) !important;
        }
        .modal-header, .modal-footer {
          border-color: var(--chatoo-gray-light) !important;
        }
        .modal-body h6 {
          color: var(--chatoo-text-secondary);
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .font-monospace {
          font-family: 'Courier New', monospace;
          font-size: 0.8rem;
        }
      `}</style>
    </div>
  );
};


export default AdminPayments;
